/**
 * Edit profile + Preferences
 * ------------------------------------------------------------------
 * Both screens used to be a disabled <fieldset> submitting GET back to the
 * profile page: the fields rendered, the button looked live, and nothing was
 * ever written. They now go through assets/pf-api.js to the profile app, which
 * owns the customer metafields Liquid has no write path for.
 *
 *   PATCH <base>/v1/me                { firstName, lastName, dob, gender, phone? }
 *   PUT   <base>/v1/me/preferences    { skinType, skinConcerns, fragrancePreference }
 *
 * Email is deliberately not sent to this endpoint. It is editable beside its
 * own Verify button and must complete the contact OTP flow before this
 * ordinary profile form can be saved. The phone field carries
 * data-pf-phone-direct while phone OTP is off (owner, 2026-10-17): it is
 * checked for a 10-digit Indian mobile only (assets/pf-indian-phone.js keeps
 * its native validity) and sent as +91XXXXXXXXXX when it changed.
 *
 * The option values the form already produces are Shopify handles — "normal",
 * "dark-spots", "citrus-green", "prefer-not-to-say" — and those are exactly
 * the enums the app validates against, so nothing is re-mapped here.
 */
(function () {
  "use strict";

  function api() {
    return window.PFApi || null;
  }

  function say(form, selector, message, isError) {
    var node = form.querySelector(selector);
    if (!node) return;
    node.textContent = message || "";
    node.hidden = !message;
    node.classList.toggle("is-error", !!isError);
  }

  function values(form) {
    var data = {};
    new FormData(form).forEach(function (value, key) {
      if (data[key] === undefined) {
        data[key] = value;
        return;
      }
      // Repeated names (the skin-concern checkboxes) collect into a list.
      if (!Array.isArray(data[key])) data[key] = [data[key]];
      data[key].push(value);
    });
    return data;
  }

  function asList(value) {
    if (value === undefined || value === null || value === "") return [];
    return Array.isArray(value) ? value : [value];
  }

  function submit(form, run) {
    if (form.dataset.pfSubmitting === "true" || !form.reportValidity()) return;
    var client = api();
    if (!client || !client.isConfigured()) {
      say(
        form,
        "[data-pf-profile-error]",
        client ? client.NOT_CONNECTED_MESSAGE : "This is not connected yet.",
        true,
      );
      return;
    }

    var button = form.querySelector('[type="submit"]');
    form.dataset.pfSubmitting = "true";
    if (button) button.disabled = true;
    client.applyFieldErrors(form, null);
    say(form, "[data-pf-profile-error]", "");
    say(form, "[data-pf-profile-status]", "Saving…");

    run(client)
      .then(function () {
        say(form, "[data-pf-profile-status]", "Saved.");
        var next = form.getAttribute("data-pf-profile-success-url");
        if (next) window.location.assign(next);
      })
      .catch(function (error) {
        client.applyFieldErrors(form, error.fields);
        say(form, "[data-pf-profile-status]", "");
        say(form, "[data-pf-profile-error]", error.message, true);
      })
      .then(function () {
        form.dataset.pfSubmitting = "false";
        if (button) button.disabled = false;
      });
  }

  // The changed phone as +91XXXXXXXXXX, or "" when it is blank or unchanged.
  // Blank leaves the saved number alone.
  function directPhone(form) {
    var input = form.querySelector("[data-pf-phone-direct]");
    var phones = window.PFIndianPhone;
    if (!input || !phones) return "";
    var typed = input.value.trim();
    if (!typed || phones.error(typed)) return "";
    var phone = phones.canonical(typed);
    return phone === (input.dataset.pfContactCurrent || "").trim() ? "" : phone;
  }

  function initProfileForm(form) {
    if (form.dataset.pfBound === "true") return;
    form.dataset.pfBound = "true";

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      var pendingContact = form.querySelector(
        '[data-pf-contact-kind]:not([data-pf-phone-direct])[data-pf-contact-dirty="true"]',
      );
      if (pendingContact) {
        say(
          form,
          "[data-pf-profile-error]",
          (window.PFAccount && window.PFAccount.strings.verify_first) ||
            "Verify your changed email address or phone number before saving.",
          true,
        );
        pendingContact.focus();
        return;
      }
      var data = values(form);
      var phone = directPhone(form);
      submit(form, function (client) {
        var body = {};
        if (phone) body.phone = phone;
        if (data.first_name !== undefined)
          body.firstName = String(data.first_name).trim();
        if (data.last_name !== undefined)
          body.lastName = String(data.last_name).trim();
        if (data.date_of_birth !== undefined)
          body.dob = String(data.date_of_birth) || null;
        if (data.gender !== undefined)
          body.gender = String(data.gender) || null;
        return client.patch("/v1/me", body);
      });
    });
  }

  function initPreferencesForm(form) {
    if (form.dataset.pfBound === "true") return;
    form.dataset.pfBound = "true";

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      var data = values(form);
      submit(form, function (client) {
        return client.put("/v1/me/preferences", {
          skinType: data.skin_type || null,
          skinConcerns: asList(data.skin_concerns),
          fragrancePreference: data.fragrance_preference || null,
        });
      });
    });
  }

  function boot() {
    document
      .querySelectorAll("[data-pf-profile-form]")
      .forEach(initProfileForm);
    document
      .querySelectorAll("[data-pf-preferences-form]")
      .forEach(initPreferencesForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
})();
